import { FormControl, Grid, Typography } from '@mui/material';
import Panel from '../../../components/UI/Panel';
import State from '../../../Models/Locations/state';
import CheckboxInput from './UI/CheckboxInput';
import MultipleCheckBoxes from './UI/MultipleCheckBoxes';
import YesNoInput from './YesNoInput';
import GenericInput from './UI/GenericInput';
import usePhonesHelperMethods from '../../../hooks/UI/usePhonesHelperMethods';
import GenericSelectInput from './UI/GenericSelectInput';
import { title } from '../../../assets/styles/appStyles';

export default function BillingInfo({
  form,
  states,
  paymentMethods,
}: Readonly<{
  form: any;
  states: State[];
  paymentMethods?: any[];
}>) {
  const { formatPhoneNumberOnChange } = usePhonesHelperMethods();

  // ---- Helpers (reduce repetition / cognitive complexity) ----
  const BI = 'custodianDetails.billingInformation';

  const getV = (k: string) =>
    form?.values?.custodianDetails?.billingInformation?.[k];

  const getT = (k: string) =>
    form?.touched?.custodianDetails?.billingInformation?.[k];

  const getE = (k: string) =>
    form?.errors?.custodianDetails?.billingInformation?.[k];

  const pathOf = (k: string) => `${BI}.${k}`;

  const setVal = (k: string, v: any) => form.setFieldValue(pathOf(k), v);
  const setTouched = (k: string) => form.setFieldTouched(pathOf(k), true);

  const errorMsg = (k: string) => (getT(k) && getE(k) ? getE(k) : '');
  const hasError = (k: string) => !!(getT(k) && getE(k));

  const checkboxClickHandler = (id: string, keyName: string) => {
    const list = [...(getV(keyName) || [])];
    const idx = (getV(keyName) || []).indexOf(id);
    if (idx > -1) list.splice(idx, 1);
    else list.push(id);
    setVal(keyName, list);
  };

  // Reusable field renderer for numeric/text inputs
  const renderInput = ({
    name,
    label,
    placeholder,
    maxLength,
    inputMode,
  }: {
    name: string;
    label: string;
    placeholder: string;
    maxLength: number;
    inputMode?:
      | 'search'
      | 'text'
      | 'email'
      | 'tel'
      | 'url'
      | 'none'
      | 'numeric'
      | 'decimal';
  }) => (
    <GenericInput
      name={name}
      label={label}
      placeholder={placeholder}
      maxLength={maxLength}
      inputMode={inputMode}
      value={getV(name)}
      onChange={(e: any) => setVal(name, e.target.value)}
      errorMsg={errorMsg(name)}
      hasError={hasError(name)}
      onBlur={() => setTouched(name)}
    />
  );

  return (
    <>
      <Panel headerText="Invoicing Details">
        <Grid spacing={2} mx={1} px={1} container>
          <Grid item xs={6} p={2}>
            <MultipleCheckBoxes
              label="Invoicing Payment method"
              selectedValues={getV('invoicePaymentMethods')}
              toggleChange={(id: string) =>
                checkboxClickHandler(id, 'invoicePaymentMethods')
              }
              keyName="invoicePaymentMethods"
              options={paymentMethods ?? []}
            />
          </Grid>
          <Grid item xs={6} p={2}>
            <FormControl fullWidth>
              <Typography sx={title}>Invoicing</Typography>
              <CheckboxInput
                keyName={`${BI}.invoiceBeforeRecords`}
                label="Invoice before records"
                checked={getV('invoiceBeforeRecords')}
                onChange={(e: any) =>
                  setVal('invoiceBeforeRecords', e.target.checked)
                }
                onBlur={() => setTouched('invoiceBeforeRecords')}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Panel>

      <Panel headerText="Fees Details">
        <Grid spacing={2} mx={1} px={1} container>
          <Grid item xs={6} p={2}>
            {renderInput({
              name: 'processingFeesAuthorization',
              label: 'Processing Fees (Authorization)',
              placeholder: 'Processing Fees (Authorization)',
              maxLength: 16,
              inputMode: 'numeric',
            })}
          </Grid>

          <Grid item xs={6} p={2}>
            {renderInput({
              name: 'processingFeesSubpoena',
              label: 'Processing Fees (Subpoena)',
              placeholder: 'Processing Fees (Subpoena)',
              maxLength: 16,
              inputMode: 'numeric',
            })}
          </Grid>

          <Grid item xs={12} p={2}>
            <MultipleCheckBoxes
              label="Processing fees method"
              keyName="processingFeesMethods"
              selectedValues={getV('processingFeesMethods')}
              toggleChange={(id: string) =>
                checkboxClickHandler(id, 'processingFeesMethods')
              }
              options={paymentMethods ?? []}
            />
          </Grid>

          <Grid item xs={6} px={2}>
            <YesNoInput
              formik={form}
              fieldName="initialProcessingFee"
              keyname={`${BI}.initialProcessingFee`}
              label="Initial Processing Fee"
            />
            {getV('initialProcessingFee') && (
              <GenericInput
                name="initialProcessingFeeNotes"
                label="Notes"
                placeholder="Notes"
                maxLength={150}
                value={getV('initialProcessingFeeNotes')}
                onChange={(e: any) =>
                  setVal('initialProcessingFeeNotes', e.target.value)
                }
                errorMsg={errorMsg('initialProcessingFeeNotes')}
                hasError={hasError('initialProcessingFeeNotes')}
                onBlur={() => setTouched('initialProcessingFeeNotes')}
              />
            )}
          </Grid>
        </Grid>
      </Panel>

      <Panel headerText="Payee Details">
        <Grid spacing={2} mx={1} px={1} container>
          <Grid item xs={4} px={2}>
            {renderInput({
              name: 'payeeName',
              label: 'Payee Name',
              placeholder: 'Payee Name',
              maxLength: 120,
            })}
          </Grid>

          <Grid item xs={4} px={2}>
            <GenericInput
              name="payeeContact"
              label="Payee Phone"
              maxLength={12}
              placeholder="xxx-xxx-xxxx"
              inputMode="tel"
              value={getV('payeeContact')}
              onChange={(e: any) => {
                formatPhoneNumberOnChange(e);
                setVal('payeeContact', e.target.value);
              }}
              errorMsg={errorMsg('payeeContact')}
              hasError={hasError('payeeContact')}
              onBlur={() => setTouched('payeeContact')}
            />
          </Grid>

          <Grid item xs={4} px={2}>
            {renderInput({
              name: 'payeeAddress',
              label: 'Payee Address',
              placeholder: 'Payee Address',
              maxLength: 120,
            })}
          </Grid>

          <Grid item xs={4} px={2}>
            {renderInput({
              name: 'payeeCity',
              label: 'Payee City',
              placeholder: 'Payee City',
              maxLength: 40,
            })}
          </Grid>

          <Grid item xs={4} px={2}>
            <GenericSelectInput
              name="payeeStateId"
              label="Payee State"
              value={getV('payeeStateId')}
              onChange={(e: any) => setVal('payeeStateId', e)}
              errorMsg={errorMsg('payeeStateId')}
              hasError={hasError('payeeStateId')}
              onBlur={() => setTouched('payeeStateId')}
              options={states}
            />
          </Grid>

          <Grid item xs={4} px={2}>
            {renderInput({
              name: 'payeePostCode',
              label: 'Zip / Postal Code',
              placeholder: 'Zip / Postal Code',
              maxLength: 5,
            })}
          </Grid>
        </Grid>
      </Panel>
    </>
  );
}
